import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  MenuItem,
  Select,
  Chip,
  Snackbar,
  Alert,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { isDarkMode } from "../../utils/constants";
import {
  clearUserTradeProposals,
  getUserTradeProposals,
  updateTradeProposalTracking,
} from "../../api/user";
import { getDenomination, isForexAssetType } from "./Charting/utils";
import { railsToIana } from '../../utils/timezones';
import { useNavigate } from "react-router-dom";

const DEFAULT_PRICE_FORMATTER = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});
const FOREX_PRICE_FORMATTER = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 5,
  maximumFractionDigits: 5,
});
const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const TRADE_STATS_WINDOWS = [
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last month" },
  { value: "all", label: "All time" },
];
const SIDEBAR_ACCOUNT_FOOTER_SAFE_GAP = 16; //don't change, this is the correct value.
const TRADE_NOTICE_STORAGE_KEY = "warp:trade-proposal-notices:v1";
const RECENT_TRADE_EVENT_WINDOW_MS = 7 * ONE_DAY_MS;

const toNonEmptyString = (value) => {
  if (value == null) return null;
  const str = String(value).trim();
  return str.length ? str : null;
};

const formatDuration = (value) => {
  const str = toNonEmptyString(value);
  if (!str) return null;
  const normalized = str.replace(/[_-]+/g, " ");
  return normalized.replace(/\b\w/g, (c) => c.toUpperCase());
};

const formatPrice = (value, assetType) => {
  if (value == null || value === "") return null;
  const num = typeof value === "number" ? value : Number(value);
  const denom = getDenomination(assetType);
  if (Number.isFinite(num)) {
    const formatter = isForexAssetType(assetType)
      ? FOREX_PRICE_FORMATTER
      : DEFAULT_PRICE_FORMATTER;
    return `${denom}${formatter.format(num)}`;
  }
  return toNonEmptyString(value);
};

const formatPercent = (value) => {
  if (value == null || value === "") return null;
  const num = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(num)) return null;
  return `${num.toFixed(2)}%`;
};

const formatSignedPercent = (value) => {
  if (value == null || value === "") return null;
  const num = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(num)) return null;
  const base = `${num.toFixed(2)}%`;
  return num > 0 ? `+${base}` : base;
};

const formatDate = (value, tz) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: tz || Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
};

const toTradeNumber = (value) => {
  if (value == null || value === "") return null;
  const num = typeof value === "number" ? value : Number(value);
  return Number.isFinite(num) ? num : null;
};

const getProposalEntryPrice = (proposal) =>
  toTradeNumber(proposal?.entryPrice ?? proposal?.entry_price);

const getProposalStopLoss = (proposal) =>
  toTradeNumber(proposal?.stopLoss ?? proposal?.stop_loss);

const getProposalTarget = (proposal) =>
  toTradeNumber(proposal?.priceTarget ?? proposal?.price_target);

const inferTradeSide = (proposal) => {
  const entry = getProposalEntryPrice(proposal);
  if (entry == null) return null;

  const stop = getProposalStopLoss(proposal);
  if (stop != null && stop !== entry) {
    if (stop < entry) return "long";
    if (stop > entry) return "short";
  }

  const target = getProposalTarget(proposal);
  if (target != null && target !== entry) {
    if (target > entry) return "long";
    if (target < entry) return "short";
  }

  return null;
};

const computeDirectionalPercentChange = (entryPrice, endPrice, tradeSide) => {
  if (!Number.isFinite(entryPrice) || entryPrice === 0 || !Number.isFinite(endPrice)) {
    return null;
  }

  const raw = ((endPrice - entryPrice) / entryPrice) * 100;
  return tradeSide === "short" ? -raw : raw;
};

const getProposalPercentChange = (proposal) => {
  const status = getNormalizedStatus(proposal);
  if (status === "pending_activation") return null;

  const exitReason = getNormalizedExitReason(proposal);
  const tradeSide = inferTradeSide(proposal);
  const entry = getProposalEntryPrice(proposal);

  if (status === "closed" && tradeSide && Number.isFinite(entry)) {
    if (exitReason === "profit_target") {
      const target = getProposalTarget(proposal);
      const realized = computeDirectionalPercentChange(entry, target, tradeSide);
      if (realized != null) return realized;
    }

    if (exitReason === "stop_loss") {
      const stop = getProposalStopLoss(proposal);
      const realized = computeDirectionalPercentChange(entry, stop, tradeSide);
      if (realized != null) return realized;
    }
  }

  return toTradeNumber(
    proposal?.percentChangeFromEntry ?? proposal?.percent_change_from_entry,
  );
};

const formatPromptEntryPrice = (value, assetType) => {
  if (value == null || value === "") return null;
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  return num.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: isForexAssetType(assetType) ? 5 : 4,
  });
};

const getNormalizedStatus = (proposal) =>
  toNonEmptyString(proposal?.status)?.toLowerCase();

const getNormalizedExitReason = (proposal) =>
  toNonEmptyString(proposal?.exitReason ?? proposal?.exit_reason)?.toLowerCase();

const formatStatusLabel = (status) => {
  const normalized = toNonEmptyString(status)?.toLowerCase();
  if (normalized === "pending_activation") return "Pending";
  return null;
};

const getPendingActivationCondition = (proposal, assetType) => {
  const status = getNormalizedStatus(proposal);
  if (status !== "pending_activation") return null;

  const entry = getProposalEntryPrice(proposal);
  const proposedAt = toTradeNumber(
    proposal?.livePriceAtSuggested ?? proposal?.live_price_at_suggested,
  );
  const formattedEntry = formatPrice(
    proposal?.entryPrice ?? proposal?.entry_price,
    assetType,
  );

  if (!Number.isFinite(entry) || !Number.isFinite(proposedAt)) {
    return formattedEntry ? `Touch ${formattedEntry}` : "Touch entry";
  }

  if (Math.abs(entry - proposedAt) <= 0.01) {
    return formattedEntry ? `Touch ${formattedEntry}` : "Touch entry";
  }

  const comparator = proposedAt < entry ? ">=" : "<=";
  return formattedEntry ? `${comparator} ${formattedEntry}` : `Touch ${comparator} entry`;
};

const getStatusSortOrder = (proposal) => {
  const status = getNormalizedStatus(proposal);
  if (status === "pending_activation") return 0;
  if (status === "active") return 1;
  if (status === "closed") return 2;
  return 3;
};

const getClosedTradeOutcome = (proposal) => {
  const status = getNormalizedStatus(proposal);
  if (status !== "closed") return null;

  const exitReason = getNormalizedExitReason(proposal);
  if (exitReason === "profit_target") return "win";
  if (exitReason === "stop_loss") return "loss";

  const change = getProposalPercentChange(proposal);
  if (change != null) {
    if (change > 0) return "win";
    if (change < 0) return "loss";
  }

  return null;
};

const getClosedExitValue = (proposal, assetType) => {
  const exitReason = getNormalizedExitReason(proposal);

  if (exitReason === "profit_target") {
    return (
      formatPrice(proposal?.priceTarget ?? proposal?.price_target, assetType) ||
      formatPrice(proposal?.currentPrice ?? proposal?.current_price, assetType)
    );
  }

  if (exitReason === "stop_loss") {
    return (
      formatPrice(proposal?.stopLoss ?? proposal?.stop_loss, assetType) ||
      formatPrice(proposal?.currentPrice ?? proposal?.current_price, assetType)
    );
  }

  return formatPrice(proposal?.currentPrice ?? proposal?.current_price, assetType);
};

const getProposalSortTimestamp = (proposal) => {
  const createdAt = Date.parse(
    toNonEmptyString(proposal?.createdAt ?? proposal?.created_at) || "",
  );
  if (Number.isFinite(createdAt)) return createdAt;

  const updatedAt = Date.parse(
    toNonEmptyString(proposal?.updatedAt ?? proposal?.updated_at) || "",
  );
  return Number.isFinite(updatedAt) ? updatedAt : 0;
};

const getProposalClosedTimestamp = (proposal) => {
  const updatedAt = Date.parse(
    toNonEmptyString(proposal?.updatedAt ?? proposal?.updated_at) || "",
  );
  if (Number.isFinite(updatedAt)) return updatedAt;

  const createdAt = Date.parse(
    toNonEmptyString(proposal?.createdAt ?? proposal?.created_at) || "",
  );
  return Number.isFinite(createdAt) ? createdAt : NaN;
};

const getLatestEventKind = (proposal) =>
  toNonEmptyString(proposal?.latestEventKind ?? proposal?.latest_event_kind)?.toLowerCase();

const getLatestEventAtIso = (proposal) =>
  toNonEmptyString(proposal?.latestEventAt ?? proposal?.latest_event_at);

const getLatestEventAtMs = (proposal) => {
  const raw = getLatestEventAtIso(proposal);
  if (!raw) return NaN;
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : NaN;
};

const isRecentTradeEvent = (proposal) => {
  const eventAtMs = getLatestEventAtMs(proposal);
  if (!Number.isFinite(eventAtMs)) return false;
  return (Date.now() - eventAtMs) <= RECENT_TRADE_EVENT_WINDOW_MS;
};

const getTradeEventLabel = (eventKind) => {
  switch (eventKind) {
    case "activated":
      return "Activated";
    case "profit_target_hit":
      return "Target hit";
    case "stop_loss_hit":
      return "Stop loss hit";
    default:
      return null;
  }
};

const getTradeEventSeverity = (eventKind) => {
  switch (eventKind) {
    case "profit_target_hit":
      return "success";
    case "stop_loss_hit":
      return "warning";
    default:
      return "info";
  }
};

const buildTradeNoticeKey = ({ proposalId, eventKind, eventAt }) => {
  if (proposalId == null || !eventKind || !eventAt) return null;
  return `${proposalId}:${eventKind}:${eventAt}`;
};

const buildTradeNoticeFromProposal = (proposal) => {
  const eventKind = getLatestEventKind(proposal);
  const eventAt = getLatestEventAtIso(proposal);
  const label = getTradeEventLabel(eventKind);
  const ticker = proposal?.ticker || proposal?.name || "Trade";
  if (!eventKind || !eventAt || !label) return null;

  let message = `${ticker} update available.`;
  if (eventKind === "activated") {
    message = `${ticker} moved from pending to active.`;
  } else if (eventKind === "profit_target_hit") {
    message = `${ticker} hit profit target and was closed.`;
  } else if (eventKind === "stop_loss_hit") {
    message = `${ticker} hit stop loss and was closed.`;
  }

  return {
    key: buildTradeNoticeKey({
      proposalId: proposal?.id,
      eventKind,
      eventAt,
    }),
    proposalId: proposal?.id,
    chatMessageId: proposal?.chatMessageId ?? proposal?.chat_message_id,
    eventKind,
    title: label,
    message,
    severity: getTradeEventSeverity(eventKind),
    eventAtMs: getLatestEventAtMs(proposal),
  };
};

const buildTradeNoticeFromEvent = (detail) => {
  if (!detail?.tradeProposalId || !detail?.event || !detail?.eventAt) return null;

  return {
    key: buildTradeNoticeKey({
      proposalId: detail.tradeProposalId,
      eventKind: detail.event,
      eventAt: detail.eventAt,
    }),
    proposalId: detail.tradeProposalId,
    chatMessageId: detail.chatMessageId,
    eventKind: detail.event,
    title: getTradeEventLabel(detail.event) || "Trade update",
    message: detail.message || `${detail.ticker || "Trade"} updated.`,
    severity: getTradeEventSeverity(detail.event),
    eventAtMs: Date.parse(detail.eventAt),
  };
};

const getStatsWindowStart = (windowKey) => {
  const now = Date.now();
  if (windowKey === "7d") return now - (7 * ONE_DAY_MS);
  if (windowKey === "30d") return now - (30 * ONE_DAY_MS);
  return null;
};

export default function TradeProposalsPanel({
  userData,
  onQuestionSubmit,
  setActivePanel,
}) {
  const userTimezone = railsToIana(userData?.user_account_info?.timezone);
  const navigate = useNavigate();
  const [tradeProposals, setTradeProposals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [clearingScope, setClearingScope] = useState(null);
  const [trackingProposalId, setTrackingProposalId] = useState(null);
  const [highlightChatMessageId, setHighlightChatMessageId] = useState(null);
  const [statsWindow, setStatsWindow] = useState("7d");
  const [tradeNoticeQueue, setTradeNoticeQueue] = useState([]);
  const highlightTimeoutRef = useRef(null);
  const listRef = useRef(null);
  const seenTradeNoticeKeysRef = useRef(null);
  const proposalNoticeKeyByIdRef = useRef(new Map());
  const hydratedTradeNoticeStateRef = useRef(false);

  const readSeenTradeNoticeKeys = () => {
    if (seenTradeNoticeKeysRef.current) return seenTradeNoticeKeysRef.current;

    if (typeof window === "undefined") {
      seenTradeNoticeKeysRef.current = new Set();
      return seenTradeNoticeKeysRef.current;
    }

    try {
      const raw = window.sessionStorage.getItem(TRADE_NOTICE_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      seenTradeNoticeKeysRef.current = new Set(Array.isArray(parsed) ? parsed : []);
    } catch {
      seenTradeNoticeKeysRef.current = new Set();
    }

    return seenTradeNoticeKeysRef.current;
  };

  const persistSeenTradeNoticeKeys = () => {
    if (typeof window === "undefined") return;
    const seen = Array.from(readSeenTradeNoticeKeys()).slice(-100);
    window.sessionStorage.setItem(TRADE_NOTICE_STORAGE_KEY, JSON.stringify(seen));
  };

  const triggerTradeHighlight = (chatMessageId) => {
    if (chatMessageId == null) return;
    const nextId = String(chatMessageId);
    setHighlightChatMessageId(nextId);
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
    }
    highlightTimeoutRef.current = setTimeout(() => {
      setHighlightChatMessageId((current) =>
        current === nextId ? null : current,
      );
    }, 6000);
  };

  const enqueueTradeNotice = (notice) => {
    if (!notice?.key) return;

    const seen = readSeenTradeNoticeKeys();
    if (seen.has(notice.key)) return;

    seen.add(notice.key);
    persistSeenTradeNoticeKeys();

    if (notice.chatMessageId != null) {
      triggerTradeHighlight(notice.chatMessageId);
    }

    setTradeNoticeQueue((prev) => {
      if (prev.some((item) => item.key === notice.key)) {
        return prev;
      }
      return [...prev, notice];
    });
  };

  const syncTradeProposalNotices = (nextProposals) => {
    const previousNoticeKeys = proposalNoticeKeyByIdRef.current;
    const nextNoticeKeys = new Map();
    const candidateNotices = (Array.isArray(nextProposals) ? nextProposals : [])
      .map((proposal) => buildTradeNoticeFromProposal(proposal))
      .filter((notice) => notice && Number.isFinite(notice.eventAtMs))
      .filter((notice) => notice.eventAtMs >= (Date.now() - RECENT_TRADE_EVENT_WINDOW_MS))
      .sort((left, right) => right.eventAtMs - left.eventAtMs);

    candidateNotices.forEach((notice) => {
      nextNoticeKeys.set(String(notice.proposalId), notice.key);
    });

    const isInitialHydration = !hydratedTradeNoticeStateRef.current;
    const noticesToQueue = candidateNotices.filter((notice) => {
      const previousKey = previousNoticeKeys.get(String(notice.proposalId));
      return previousKey !== notice.key;
    });

    proposalNoticeKeyByIdRef.current = nextNoticeKeys;
    hydratedTradeNoticeStateRef.current = true;

    if (isInitialHydration) {
      if (noticesToQueue[0]) enqueueTradeNotice(noticesToQueue[0]);
      return;
    }

    noticesToQueue.forEach(enqueueTradeNotice);
  };

  const fetchTrades = async () => {
    if (!userData?.user_id) return;
    setLoading(true);
    const res = await getUserTradeProposals();
    if (res && !res.error) {
      const nextTrades = Array.isArray(res) ? res : [];
      setTradeProposals(nextTrades);
      syncTradeProposalNotices(nextTrades);
    } else {
      setTradeProposals([]);
      syncTradeProposalNotices([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!userData?.user_id) return;

    fetchTrades();
  }, [userData?.user_id]);

  useEffect(() => {
    const handler = () => fetchTrades();
    window.addEventListener("trade-proposals:updated", handler);
    return () => window.removeEventListener("trade-proposals:updated", handler);
  }, [userData?.user_id]);

  const handleClearTrades = async (scope) => {
    if (!userData?.user_id || clearingScope) return;

    const isPastScope = scope === "past";
    const tradeCount = Array.isArray(tradeProposals)
      ? tradeProposals.filter((proposal) => {
          const isClosed = getNormalizedStatus(proposal) === "closed";
          return isPastScope ? isClosed : !isClosed;
        }).length
      : 0;
    if (tradeCount <= 0) return;

    const tradeLabel = tradeCount === 1 ? "trade" : "trades";
    const confirmationMessage = isPastScope
      ? `Clear ${tradeCount} past ${tradeLabel} from this panel?`
      : `Clear ${tradeCount} current ${tradeLabel} from this panel?`;
    if (typeof window !== "undefined" && !window.confirm(confirmationMessage)) {
      return;
    }

    setClearingScope(scope);
    try {
      const result = await clearUserTradeProposals(scope);
      if (result?.error) {
        console.error("Failed to clear trade proposals:", result.error);
        return;
      }

      await fetchTrades();
    } finally {
      setClearingScope(null);
    }
  };

  const handleStopTrackingTrade = async (proposal) => {
    if (!proposal?.chatMessageId || trackingProposalId != null) return;

    const tradeLabel =
      toNonEmptyString(proposal.ticker) ||
      toNonEmptyString(proposal.name) ||
      "this trade";
    const confirmationMessage = `Stop tracking this ${tradeLabel} trade?`;
    if (typeof window !== "undefined" && !window.confirm(confirmationMessage)) {
      return;
    }

    setTrackingProposalId(proposal.id);
    try {
      const result = await updateTradeProposalTracking(proposal.chatMessageId, false);
      if (result?.error) {
        console.error("Failed to stop tracking trade proposal:", result.error);
        return;
      }

      await fetchTrades();
    } finally {
      setTrackingProposalId(null);
    }
  };

  useEffect(() => {
    const handler = (event) =>
      triggerTradeHighlight(event?.detail?.chatMessageId);
    window.addEventListener("trade-proposals:highlight", handler);

    if (typeof window !== "undefined") {
      const pending = window.__warpTradeProposalHighlight;
      if (pending?.chatMessageId != null) {
        triggerTradeHighlight(pending.chatMessageId);
        window.__warpTradeProposalHighlight = null;
      }
    }

    return () => {
      window.removeEventListener("trade-proposals:highlight", handler);
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
        highlightTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const handler = (event) => {
      const notice = buildTradeNoticeFromEvent(event?.detail);
      if (notice) {
        enqueueTradeNotice(notice);
      }
    };

    window.addEventListener("trade-proposals:event", handler);
    return () => window.removeEventListener("trade-proposals:event", handler);
  }, []);

  useEffect(() => {
    if (!highlightChatMessageId || !listRef.current) return;
    const target = listRef.current.querySelector(
      `[data-chat-message-id="${highlightChatMessageId}"]`,
    );
    if (target?.scrollIntoView) {
      target.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlightChatMessageId, tradeProposals]);

  const sortedTradeProposals = useMemo(() => {
    if (!Array.isArray(tradeProposals)) return [];
    return [...tradeProposals].sort((a, b) => {
      const statusOrderDelta = getStatusSortOrder(a) - getStatusSortOrder(b);
      if (statusOrderDelta !== 0) return statusOrderDelta;

      return getProposalSortTimestamp(b) - getProposalSortTimestamp(a);
    });
  }, [tradeProposals]);

  const pendingTradeProposals = useMemo(
    () => sortedTradeProposals.filter((proposal) => getNormalizedStatus(proposal) === "pending_activation"),
    [sortedTradeProposals],
  );

  const activeTradeProposals = useMemo(
    () => sortedTradeProposals.filter((proposal) => getNormalizedStatus(proposal) === "active"),
    [sortedTradeProposals],
  );

  const completedTradeProposals = useMemo(
    () => sortedTradeProposals.filter((proposal) => getNormalizedStatus(proposal) === "closed"),
    [sortedTradeProposals],
  );

  const openTradeProposals = useMemo(
    () => [...pendingTradeProposals, ...activeTradeProposals],
    [pendingTradeProposals, activeTradeProposals],
  );

  const tradeRows = useMemo(() => {
    const rows = [];

    const appendSection = (kind, title, proposalsForSection) => {
      if (proposalsForSection.length === 0) return;

      rows.push({ kind: "section", title, key: `${kind}-heading` });
      proposalsForSection.forEach((proposal, index) => {
        rows.push({
          kind: "proposal",
          proposal,
          key: `proposal-${kind}-${proposal.id ?? proposal.chatMessageId ?? getProposalSortTimestamp(proposal)}-${index}`,
        });
      });
    };

    appendSection("pending", "Pending Activation", pendingTradeProposals);
    appendSection("active", "Active Trades", activeTradeProposals);
    appendSection("completed", "Completed Trades", completedTradeProposals);

    return rows;
  }, [pendingTradeProposals, activeTradeProposals, completedTradeProposals]);

  const closedTradesForStats = useMemo(() => {
    const start = getStatsWindowStart(statsWindow);
    return completedTradeProposals.filter((proposal) => {
      if (start == null) return true;
      const closedAt = getProposalClosedTimestamp(proposal);
      return Number.isFinite(closedAt) && closedAt >= start;
    });
  }, [completedTradeProposals, statsWindow]);

  const tradeStats = useMemo(() => {
    const pnlValues = closedTradesForStats
      .map((proposal) => getProposalPercentChange(proposal))
      .filter((value) => Number.isFinite(value));

    const measurableCount = pnlValues.length;
    const closedCount = closedTradesForStats.length;
    const winners = pnlValues.filter((value) => value > 0);
    const losers = pnlValues.filter((value) => value < 0);
    const sum = pnlValues.reduce((acc, value) => acc + value, 0);
    const grossProfit = winners.reduce((acc, value) => acc + value, 0);
    const grossLoss = losers.reduce((acc, value) => acc + value, 0);
    const grossLossAbs = Math.abs(grossLoss);
    const averageReturn = measurableCount > 0 ? sum / measurableCount : null;
    const averageWin = winners.length > 0
      ? grossProfit / winners.length
      : null;
    const averageLoss = losers.length > 0
      ? grossLoss / losers.length
      : null;
    const winRate = measurableCount > 0
      ? (winners.length / measurableCount) * 100
      : null;
    let profitFactor = null;
    if (grossLossAbs > 0) {
      profitFactor = grossProfit / grossLossAbs;
    } else if (grossProfit > 0) {
      profitFactor = Number.POSITIVE_INFINITY;
    }

    return {
      closedCount,
      measurableCount,
      averageReturn,
      averageWin,
      averageLoss,
      winRate,
      profitFactor,
      expectancy: averageReturn,
    };
  }, [closedTradesForStats]);

  const statsItems = useMemo(() => {
    const formatRatio = (value) => {
      if (value == null) return "—";
      if (value === Number.POSITIVE_INFINITY) return "∞";
      return value.toFixed(2);
    };

    return [
      { label: "Closed", value: `${tradeStats.closedCount}` },
      {
        label: "Measured",
        value: `${tradeStats.measurableCount}`,
      },
      {
        label: "Win Rate",
        value: formatPercent(tradeStats.winRate) || "—",
      },
      {
        label: "Avg Return",
        value: formatSignedPercent(tradeStats.averageReturn) || "—",
      },
      {
        label: "Avg Win",
        value: formatSignedPercent(tradeStats.averageWin) || "—",
      },
      {
        label: "Avg Loss",
        value: formatSignedPercent(tradeStats.averageLoss) || "—",
      },
      {
        label: "Profit Factor",
        value: formatRatio(tradeStats.profitFactor),
      },
      {
        label: "EV / Trade",
        value: formatSignedPercent(tradeStats.expectancy) || "—",
      },
    ];
  }, [tradeStats]);

  const tradePanelColors = isDarkMode
    ? {
        bg: "#131521",
        border: "rgba(255,255,255,0.08)",
        text: "#F6F6F6",
        subtext: "rgba(255,255,255,0.7)",
        changeProfit: "rgba(21, 128, 61, 0.84)",
        changeLoss: "rgba(185, 28, 28, 0.82)",
        divider: "rgba(255,255,255,0.08)",
        itemHover: "rgba(255,255,255,0.04)",
        highlightBg: "rgba(99,102,241,0.12)",
        highlightBorder: "rgba(129,140,248,0.7)",
        highlightShadow: "0 0 0 1px rgba(129,140,248,0.55)",
        pendingBg: "rgba(245,158,11,0.14)",
        pendingBorder: "rgba(245,158,11,0.5)",
        pendingText: "#FBBF24",
        completedWinBg: "rgba(34,197,94,0.14)",
        completedWinBorder: "rgba(34,197,94,0.55)",
        completedLossBg: "rgba(239,68,68,0.14)",
        completedLossBorder: "rgba(239,68,68,0.55)",
      }
      : {
        bg: "#F6F6F6",
        border: "#e5e7eb",
        text: "#1D1D1F",
        subtext: "rgba(0,0,0,0.6)",
        changeProfit: "#15803D",
        changeLoss: "#B91C1C",
        divider: "rgba(0,0,0,0.08)",
        itemHover: "rgba(0,0,0,0.04)",
        highlightBg: "rgba(59,130,246,0.08)",
        highlightBorder: "rgba(59,130,246,0.6)",
        highlightShadow: "0 0 0 1px rgba(59,130,246,0.45)",
        pendingBg: "rgba(245,158,11,0.12)",
        pendingBorder: "rgba(245,158,11,0.55)",
        pendingText: "#B45309",
        completedWinBg: "rgba(34,197,94,0.12)",
        completedWinBorder: "rgba(34,197,94,0.55)",
        completedLossBg: "rgba(239,68,68,0.12)",
        completedLossBorder: "rgba(239,68,68,0.55)",
      };

  return (
    <Box
      sx={{
        height: `calc(100% - ${SIDEBAR_ACCOUNT_FOOTER_SAFE_GAP}px - env(safe-area-inset-bottom, 0px))`,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        px: 2,
        pt: 1,
        pb: 2,
      }}
    >
      <Box
        sx={{
          p: 1,
          borderRadius: 2,
          border: `1px solid ${tradePanelColors.border}`,
          backgroundColor: tradePanelColors.bg,
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            mb: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography
              sx={{
                color: tradePanelColors.text,
                fontWeight: 700,
                fontSize: "0.98rem",
              }}
            >
              Trade Actions
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => navigate("/account")}
              sx={{
                textTransform: "none",
                whiteSpace: "normal",
                textAlign: "center",
                lineHeight: 1.15,
                flexShrink: 0,
                minWidth: 0,
                maxWidth: "100%",
                px: 1.2,
                py: 0.55,
                borderColor: tradePanelColors.divider,
                color: tradePanelColors.subtext,
                "&:hover": {
                  borderColor: tradePanelColors.highlightBorder,
                  backgroundColor: tradePanelColors.highlightBg,
                },
              }}
            >
              Notification settings
            </Button>
          </Box>
          <Typography
            variant="caption"
            sx={{
              color: tradePanelColors.subtext,
              lineHeight: 1.45,
              display: "block",
              mt: 0.5,
            }}
          >
            In-app trade notices stay on. Email and push can be managed in Account.
          </Typography>
        </Box>

        <Divider sx={{ mb: 1, borderColor: tradePanelColors.divider }} />

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              pr: 0.5,
            }}
            ref={listRef}
          >
            {loading && (
              <Typography variant="caption" sx={{ color: tradePanelColors.subtext }}>
                Loading trade proposals…
              </Typography>
            )}

            {!loading && (!tradeProposals || tradeProposals.length === 0) && (
              <Typography variant="caption" sx={{ color: tradePanelColors.subtext }}>
                No trade proposals yet.
              </Typography>
            )}

            {!loading && sortedTradeProposals.length > 0 && tradeRows.map((row) => {
              if (row.kind === "section") {
                return (
                  <Box
                    key={row.key}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mt: 0.5,
                      mb: 0.25,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: tradePanelColors.subtext,
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        fontSize: "0.62rem",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row.title}
                    </Typography>
                    <Divider sx={{ flex: 1, borderColor: tradePanelColors.divider }} />
                  </Box>
                );
              }

              const proposal = row.proposal;
              const isHighlighted =
                highlightChatMessageId != null &&
                String(proposal.chatMessageId) === highlightChatMessageId;
              const normalizedStatus = getNormalizedStatus(proposal);
              const isPendingTrade = normalizedStatus === "pending_activation";
              const isClosedTrade = normalizedStatus === "closed";
              const closedTradeOutcome = getClosedTradeOutcome(proposal);
              const cardToneSx = closedTradeOutcome === "win"
                  ? {
                      backgroundColor: tradePanelColors.completedWinBg,
                      borderColor: tradePanelColors.completedWinBorder,
                    }
                  : closedTradeOutcome === "loss"
                    ? {
                        backgroundColor: tradePanelColors.completedLossBg,
                        borderColor: tradePanelColors.completedLossBorder,
                      }
                    : null;
              const tickerLabel =
                proposal.ticker ||
                proposal.name ||
                `Ticker ${proposal.allDataTickerId}`;
              const tradeSide = inferTradeSide(proposal);
              const entry = formatPrice(proposal.entryPrice, proposal.assetType);
              const stopLoss = formatPrice(proposal.stopLoss, proposal.assetType);
              const priceTarget = formatPrice(
                proposal.priceTarget,
                proposal.assetType,
              );
              const duration = formatDuration(proposal.expectedTradeDuration);
              const current = formatPrice(
                proposal.currentPrice,
                proposal.assetType,
              );
              const proposedAt = formatPrice(
                proposal.livePriceAtSuggested,
                proposal.assetType,
              );
              const pnlPercent = getProposalPercentChange(proposal);
              const changeFromEntry = formatPercent(pnlPercent);
              const changeFromEntrySummary = formatSignedPercent(pnlPercent);
              const closedExit = isClosedTrade
                ? getClosedExitValue(proposal, proposal.assetType)
                : null;
              const pendingCondition = isPendingTrade
                ? getPendingActivationCondition(proposal, proposal.assetType)
                : null;
              const statusLabel = formatStatusLabel(normalizedStatus);
              const summaryChangeColor = pnlPercent == null
                ? tradePanelColors.subtext
                : pnlPercent > 0
                  ? tradePanelColors.changeProfit
                  : pnlPercent < 0
                    ? tradePanelColors.changeLoss
                    : tradePanelColors.subtext;
              const summaryParts = isClosedTrade
                ? [
                    { value: `Exit: ${closedExit || "—"}` },
                    { value: `Entry: ${entry || "—"}` },
                    { value: `P&L: ${changeFromEntrySummary || "—"}`, isChange: true },
                  ]
                : isPendingTrade
                  ? [
                      { value: `Current: ${current || "—"}` },
                      { value: `Entry: ${entry || "—"}` },
                      { value: `${pendingCondition || "Entry touch"}` },
                    ]
                : [
                    { value: `Current: ${current || "—"}` },
                    { value: `Entry: ${entry || "—"}` },
                    { value: `P&L: ${changeFromEntrySummary || "—"}`, isChange: true },
                  ];
              const rewardRisk = proposal.rewardRiskRatio
                ? proposal.rewardRiskRatio.toFixed(2)
                : null;
              const createdAt = formatDate(proposal.createdAt, userTimezone);
              const notes = toNonEmptyString(proposal.notes);
              const latestEventKind = getLatestEventKind(proposal);
              const latestEventLabel = getTradeEventLabel(latestEventKind);
              const hasRecentEvent = isRecentTradeEvent(proposal);
              const eventToneSx = hasRecentEvent
                ? latestEventKind === "profit_target_hit"
                  ? {
                      backgroundColor: tradePanelColors.completedWinBg,
                      borderColor: tradePanelColors.completedWinBorder,
                      color: tradePanelColors.changeProfit,
                    }
                  : latestEventKind === "stop_loss_hit"
                    ? {
                        backgroundColor: tradePanelColors.completedLossBg,
                        borderColor: tradePanelColors.completedLossBorder,
                        color: tradePanelColors.changeLoss,
                      }
                    : {
                        backgroundColor: tradePanelColors.highlightBg,
                        borderColor: tradePanelColors.highlightBorder,
                        color: tradePanelColors.text,
                      }
                : null;

              const detailItems = isPendingTrade
                ? [
                    { label: "Awaiting", value: pendingCondition },
                    { label: "Entry", value: entry },
                    { label: "Stop", value: stopLoss },
                    { label: "Target", value: priceTarget },
                    { label: "Duration", value: duration },
                    { label: "Current", value: current },
                    { label: "At Proposal", value: proposedAt },
                    { label: "R/R", value: rewardRisk },
                  ]
                : [
                    { label: "Entry", value: entry },
                    { label: "Stop", value: stopLoss },
                    { label: "Target", value: priceTarget },
                    { label: "Duration", value: duration },
                    { label: "Current", value: current },
                    { label: "At Proposal", value: proposedAt },
                    { label: "P&L", value: changeFromEntry },
                    { label: "R/R", value: rewardRisk },
                  ];
              const tickerId = Number(proposal.allDataTickerId);
              const canAnalyze =
                typeof onQuestionSubmit === "function" &&
                proposal?.id != null &&
                Number.isFinite(tickerId);
              const isStoppingTracking = trackingProposalId === proposal.id;

              return (
                <Accordion
                  key={row.key}
                  disableGutters
                  square
                  data-chat-message-id={proposal.chatMessageId ?? ""}
                  sx={{
                    backgroundColor:
                      eventToneSx?.backgroundColor ||
                      cardToneSx?.backgroundColor ||
                      "transparent",
                    border: `1px solid ${
                      eventToneSx?.borderColor ||
                      cardToneSx?.borderColor ||
                      tradePanelColors.divider
                    }`,
                    borderRadius: 1,
                    transition:
                      "border-color 160ms ease, box-shadow 160ms ease, background-color 160ms ease",
                    ...(isHighlighted
                      ? {
                          borderColor: tradePanelColors.highlightBorder,
                          backgroundColor: tradePanelColors.highlightBg,
                          boxShadow: tradePanelColors.highlightShadow,
                        }
                      : {}),
                    "&:before": { display: "none" },
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandMoreIcon sx={{ color: tradePanelColors.subtext }} />
                    }
                    sx={{
                      px: 1,
                      py: 0.5,
                      minHeight: "unset",
                      "& .MuiAccordionSummary-content": {
                        margin: 0,
                      },
                      "&:hover": {
                        backgroundColor: tradePanelColors.itemHover,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.25,
                        width: "100%",
                        minWidth: 0,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "baseline",
                          gap: 0.5,
                          minWidth: 0,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "baseline",
                            gap: 0.75,
                            flex: 1,
                            minWidth: 0,
                            overflow: "hidden",
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{
                              color: tradePanelColors.text,
                              fontWeight: 600,
                              minWidth: 0,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {tickerLabel}
                          </Typography>
                          {tradeSide && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: tradePanelColors.subtext,
                                fontWeight: 600,
                                letterSpacing: "0.02em",
                                whiteSpace: "nowrap",
                                textTransform: "lowercase",
                              }}
                            >
                              {tradeSide}
                            </Typography>
                          )}
                          {statusLabel && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: isPendingTrade
                                  ? tradePanelColors.pendingText
                                  : tradePanelColors.subtext,
                                fontWeight: 600,
                                letterSpacing: "0.02em",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {statusLabel}
                            </Typography>
                          )}
                          {hasRecentEvent && latestEventLabel && (
                            <Chip
                              size="small"
                              label={latestEventLabel}
                              sx={{
                                height: 20,
                                borderRadius: "999px",
                                fontSize: "0.62rem",
                                fontWeight: 700,
                                color: eventToneSx?.color || tradePanelColors.text,
                                border: `1px solid ${eventToneSx?.borderColor || tradePanelColors.divider}`,
                                backgroundColor: eventToneSx?.backgroundColor || tradePanelColors.itemHover,
                                "& .MuiChip-label": {
                                  px: 0.9,
                                },
                              }}
                            />
                          )}
                        </Box>
                        {createdAt && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: tradePanelColors.subtext,
                              flexShrink: 0,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {createdAt}
                          </Typography>
                        )}
                      </Box>
                      {summaryParts.length > 0 && (
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "auto auto 1fr",
                            alignItems: "center",
                            columnGap: 1.75,
                            minWidth: 0,
                            overflow: "hidden",
                          }}
                        >
                          {summaryParts.map((part, index) => (
                            <Typography
                              key={`${proposal.id}-summary-${index}`}
                              variant="caption"
                              sx={{
                                color: part.isChange
                                  ? summaryChangeColor
                                  : tradePanelColors.subtext,
                                fontWeight: part.isChange ? 600 : 400,
                                whiteSpace: "nowrap",
                                ...(index === 2 && {
                                  justifySelf: "end",
                                  textAlign: "right",
                                }),
                              }}
                            >
                              {part.value}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 1, pt: 0.5, pb: 1 }}>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                        gap: 0.75,
                      }}
                    >
                      {detailItems.map((item) => (
                        <Box
                          key={item.label}
                          sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: tradePanelColors.subtext,
                              textTransform: "uppercase",
                              letterSpacing: "0.08em",
                              fontSize: "0.6rem",
                              display: "inline-block",
                            }}
                          >
                            {item.label}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: tradePanelColors.text }}
                          >
                            {item.value || "—"}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                    {notes && (
                      <Box sx={{ mt: 0.75 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: tradePanelColors.subtext,
                            textTransform: "uppercase",
                            letterSpacing: "0.08em",
                            fontSize: "0.6rem",
                          }}
                        >
                          Notes
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: tradePanelColors.text,
                            display: "-webkit-box",
                            WebkitLineClamp: 4,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {notes}
                        </Typography>
                      </Box>
                    )}
                    <Box
                      sx={{
                        mt: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        flexWrap: "wrap",
                      }}
                    >
                      <Box
                        className="base-text"
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          px: 1.5,
                          py: 0.6,
                          borderRadius: "999px",
                          fontSize: 12,
                          fontWeight: 600,
                          letterSpacing: "0.02em",
                          color: tradePanelColors.text,
                          backgroundColor: tradePanelColors.itemHover,
                          border: `1px solid ${tradePanelColors.divider}`,
                          cursor: canAnalyze ? "pointer" : "default",
                          userSelect: "none",
                          "&:hover": canAnalyze
                            ? {
                                borderColor: tradePanelColors.highlightBorder,
                                backgroundColor: tradePanelColors.highlightBg,
                              }
                            : {},
                        }}
                        onClick={() => {
                          if (!canAnalyze) return;
                          const labelTicker =
                            toNonEmptyString(proposal.ticker) ||
                            toNonEmptyString(proposal.name) ||
                            `Ticker ${tickerId}`;
                          const entryForPrompt = formatPromptEntryPrice(
                            proposal.entryPrice,
                            proposal.assetType,
                          );
                          const tradeSuffix = tradeSide && entryForPrompt
                            ? `; ${tradeSide} at ${entryForPrompt}`
                            : entryForPrompt
                              ? `; at ${entryForPrompt}`
                              : "";
                          onQuestionSubmit(
                            `Analyze current ${labelTicker} trade action${tradeSuffix}`,
                            null,
                            [tickerId],
                            [],
                            { tradeProposalId: proposal.id },
                          );
                          if (typeof setActivePanel === "function") {
                            setActivePanel("chat");
                          }
                        }}
                      >
                        Analyze Trade
                      </Box>
                      <Box
                        className="base-text"
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          px: 1.5,
                          py: 0.6,
                          borderRadius: "999px",
                          fontSize: 12,
                          fontWeight: 600,
                          letterSpacing: "0.02em",
                          color: isStoppingTracking
                            ? tradePanelColors.subtext
                            : tradePanelColors.text,
                          backgroundColor: tradePanelColors.itemHover,
                          border: `1px solid ${tradePanelColors.divider}`,
                          cursor: isStoppingTracking ? "default" : "pointer",
                          userSelect: "none",
                          opacity: isStoppingTracking ? 0.7 : 1,
                          "&:hover": isStoppingTracking
                            ? {}
                            : {
                                borderColor: tradePanelColors.highlightBorder,
                                backgroundColor: tradePanelColors.highlightBg,
                              },
                        }}
                        onClick={() => {
                          if (isStoppingTracking) return;
                          handleStopTrackingTrade(proposal);
                        }}
                      >
                        {isStoppingTracking ? "Stopping..." : "Stop Tracking"}
                      </Box>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>

          <Box
            sx={{
              mt: 1,
              pt: 1,
              px: 0.25,
              borderTop: `1px solid ${tradePanelColors.divider}`,
            }}
          >
            <Box
              sx={{
                borderRadius: 1.5,
                border: `1px solid ${tradePanelColors.divider}`,
                backgroundColor: tradePanelColors.bg,
                px: 1,
                py: 0.9,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  mb: 0.9,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: tradePanelColors.subtext,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    fontSize: "0.62rem",
                    fontWeight: 600,
                  }}
                >
                  Trade Statistics
                </Typography>
                <FormControl size="small" sx={{ minWidth: 128 }}>
                  <Select
                    value={statsWindow}
                    onChange={(event) => setStatsWindow(event.target.value)}
                    sx={{
                      height: 30,
                      fontSize: "0.75rem",
                      color: tradePanelColors.text,
                      backgroundColor: isDarkMode ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
                      "& .MuiSvgIcon-root": {
                        color: tradePanelColors.subtext,
                      },
                      "& fieldset": {
                        borderColor: tradePanelColors.divider,
                      },
                      "&:hover fieldset": {
                        borderColor: tradePanelColors.highlightBorder,
                      },
                    }}
                  >
                    {TRADE_STATS_WINDOWS.map((windowOption) => (
                      <MenuItem key={windowOption.value} value={windowOption.value}>
                        {windowOption.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2, minmax(0, 1fr))",
                    sm: "repeat(4, minmax(0, 1fr))",
                  },
                  gap: 0.7,
                }}
              >
                {statsItems.map((item) => (
                  <Box
                    key={item.label}
                    sx={{
                      borderRadius: 1,
                      border: `1px solid ${tradePanelColors.divider}`,
                      backgroundColor: isDarkMode ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)",
                      px: 0.75,
                      py: 0.5,
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: tradePanelColors.subtext,
                        textTransform: "uppercase",
                        letterSpacing: "0.07em",
                        fontSize: "0.54rem",
                        display: "block",
                        lineHeight: 1.2,
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: tradePanelColors.text,
                        fontWeight: 600,
                        fontSize: "0.72rem",
                        display: "block",
                        mt: 0.2,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {item.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Box sx={{ display: "flex", gap: 0.75, mt: 0.8 }}>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={
                    clearingScope !== null || completedTradeProposals.length === 0
                  }
                  onClick={() => handleClearTrades("past")}
                  sx={{
                    flex: 1,
                    textTransform: "none",
                    fontSize: "0.7rem",
                    minHeight: 28,
                    borderColor: tradePanelColors.divider,
                    color: tradePanelColors.subtext,
                    "&:hover": {
                      borderColor: tradePanelColors.highlightBorder,
                      backgroundColor: tradePanelColors.highlightBg,
                    },
                  }}
                >
                  {clearingScope === "past" ? "Clearing..." : "Clear past trades"}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={
                    clearingScope !== null || openTradeProposals.length === 0
                  }
                  onClick={() => handleClearTrades("current")}
                  sx={{
                    flex: 1,
                    textTransform: "none",
                    fontSize: "0.7rem",
                    minHeight: 28,
                    borderColor: tradePanelColors.divider,
                    color: tradePanelColors.subtext,
                    "&:hover": {
                      borderColor: tradePanelColors.highlightBorder,
                      backgroundColor: tradePanelColors.highlightBg,
                    },
                  }}
                >
                  {clearingScope === "current" ? "Clearing..." : "Clear current trades"}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <Snackbar
        key={tradeNoticeQueue[0]?.key || "trade-notice"}
        open={tradeNoticeQueue.length > 0}
        autoHideDuration={6500}
        onClose={() => {
          setTradeNoticeQueue((prev) => prev.slice(1));
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => {
            setTradeNoticeQueue((prev) => prev.slice(1));
          }}
          severity={tradeNoticeQueue[0]?.severity || "info"}
          variant="filled"
          sx={{ width: "100%" }}
        >
          <strong>{tradeNoticeQueue[0]?.title || "Trade update"}</strong>
          <Box component="span" sx={{ display: "block", mt: 0.2 }}>
            {tradeNoticeQueue[0]?.message || "A tracked trade changed status."}
          </Box>
        </Alert>
      </Snackbar>
    </Box>
  );
}
